class TbCallPopup extends HTMLElement {
  constructor () {
    super()
    this.delaySeconds = Number.parseInt(this.dataset.delay, 10)
    if (Number.isNaN(this.delaySeconds) || this.delaySeconds < 0) {
      this.delaySeconds = 20
    }

    this.weekdayHoursStart = this.parseTime(this.dataset.weekdayHoursStart)
    this.weekdayHoursEnd = this.parseTime(this.dataset.weekdayHoursEnd)
    this.weekendHoursStart = this.parseTime(this.dataset.weekendHoursStart)
    this.weekendHoursEnd = this.parseTime(this.dataset.weekendHoursEnd)
    this.activeDays = (this.dataset.activeDays || '')
      .split(',')
      .map((d) => d.trim())
      .filter((d) => d !== '')
      .map((d) => Number.parseInt(d, 10))
      .filter((d) => !Number.isNaN(d))

    this.shopDriftMs = this.computeShopDriftMs()
    this.storageKey = `tb-call-popup-dismissed:${this.dataset.sectionId || 'default'}`

    this.timer = null
    this.opened = false

    this.handleClose = this.handleClose.bind(this)
    this.handleCallClick = this.handleCallClick.bind(this)
    this.handleChatOpen = this.handleChatOpen.bind(this)
    this.handleChatTriggerClick = this.handleChatTriggerClick.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  connectedCallback () {
    if (this.isDismissed()) {
      this.removeFromDom()
      return
    }

    if (this.relocateToChatBundleIfNeeded()) return

    this.removeAttribute('hidden')

    this.closeButton = this.querySelector('.call-popup__close')
    this.callLink = this.querySelector('.call-popup__phone-link')

    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.handleClose)
    }
    if (this.callLink) {
      this.callLink.addEventListener('click', this.handleCallClick)
    }

    window.addEventListener('chat:open', this.handleChatOpen)
    window.addEventListener('chat:toggle', this.handleChatOpen)
    document.addEventListener('click', this.handleChatTriggerClick, true)
    document.addEventListener('keydown', this.handleKeydown)

    this.scheduleOpen()
  }

  relocateToChatBundleIfNeeded () {
    const chatBundle = document.querySelector('.chat-bundle')
    if (!chatBundle) {
      this.removeFromDom()
      return true
    }
    if (this.parentNode === chatBundle) return false
    chatBundle.appendChild(this)
    return true
  }

  disconnectedCallback () {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    window.removeEventListener('chat:open', this.handleChatOpen)
    window.removeEventListener('chat:toggle', this.handleChatOpen)
    document.removeEventListener('click', this.handleChatTriggerClick, true)
    document.removeEventListener('keydown', this.handleKeydown)
  }

  parseTime (value) {
    if (!value) return null
    const match = String(value).trim().match(/^(\d{1,2}):(\d{2})$/)
    if (!match) return null
    const hours = Number.parseInt(match[1], 10)
    const minutes = Number.parseInt(match[2], 10)
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null
    return hours * 60 + minutes
  }

  computeShopDriftMs () {
    const shopNowIso = this.dataset.shopNow
    if (!shopNowIso) return 0
    const shopMs = new Date(shopNowIso).getTime()
    if (Number.isNaN(shopMs)) return 0
    return Date.now() - shopMs
  }

  getShopNow () {
    return new Date(Date.now() - this.shopDriftMs)
  }

  isWithinSchedule () {
    const now = this.getShopNow()
    const weekday = now.getDay()
    if (this.activeDays.length > 0 && !this.activeDays.includes(weekday)) {
      return false
    }

    const minutes = now.getHours() * 60 + now.getMinutes()
    const isWeekend = weekday === 0 || weekday === 6
    const hoursStart = isWeekend ? this.weekendHoursStart : this.weekdayHoursStart
    const hoursEnd = isWeekend ? this.weekendHoursEnd : this.weekdayHoursEnd

    if (hoursStart === null || hoursEnd === null) {
      return true
    }

    if (hoursStart <= hoursEnd) {
      return minutes >= hoursStart && minutes < hoursEnd
    }
    return minutes >= hoursStart || minutes < hoursEnd
  }

  isDismissed () {
    try {
      return window.sessionStorage.getItem(this.storageKey) === '1'
    } catch (_e) {
      return false
    }
  }

  setDismissed () {
    try {
      window.sessionStorage.setItem(this.storageKey, '1')
    } catch (_e) {
      /* sessionStorage unavailable */
    }
  }

  scheduleOpen () {
    if (this.timer) {
      clearTimeout(this.timer)
    }
    this.timer = setTimeout(() => {
      this.timer = null
      if (this.isDismissed()) {
        this.removeFromDom()
        return
      }
      if (!this.isWithinSchedule()) {
        this.removeFromDom()
        return
      }
      this.open()
    }, this.delaySeconds * 1000)
  }

  open () {
    if (this.opened) return
    this.opened = true
    this.setAttribute('data-open', 'true')
  }

  close ({ persist = true } = {}) {
    if (persist) this.setDismissed()
    this.opened = false
    this.setAttribute('data-open', 'false')
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
    setTimeout(() => this.removeFromDom(), 250)
  }

  removeFromDom () {
    if (this.parentNode) {
      this.parentNode.removeChild(this)
    }
  }

  handleClose (event) {
    event.preventDefault()
    this.close({ persist: true })
  }

  handleCallClick () {
    this.setDismissed()
    setTimeout(() => this.close({ persist: true }), 100)
  }

  handleChatOpen () {
    this.close({ persist: true })
  }

  handleChatTriggerClick (event) {
    const trigger = event.target.closest('.chat__trigger')
    if (!trigger) return
    if (this.contains(trigger)) return
    this.close({ persist: true })
  }

  handleKeydown (event) {
    if (event.key === 'Escape' && this.opened) {
      this.close({ persist: true })
    }
  }
}

if (!customElements.get('tb-call-popup')) {
  customElements.define('tb-call-popup', TbCallPopup)
}
